import { IconBin } from '@cimpress-technology/react-streamline-icons';
import { Button, CodeBlock, Modal, ReactTablev6, Tooltip } from '@cimpress/react-components';
import { AuthorizationCodeGrant } from '@cimpress/simple-auth-wrapper';
import React, { useEffect, useState } from 'react';
import { deleteSubscription, getSubscriptions } from '../../services/subscriptions.ts';
import './subscription.css';

type Columns = {
  Header: string;
  accessor?: string | any;
  Cell?: ({ original }: { original: any }) => any;
  maxWidth?: number;
  id?: string;
}[];

type resourceFilter = {
  path: string;
  value: string;
  operator: string;
};

type SubscriptionType = {
  id: string;
  created: string;
  version: number;
  alertInfo: {
    channel: Channel;
    details: {
      email?: string;
      httpEndpoint?: string;
      deliveryFrequency?: string;
      dayOfWeek?: number;
    };
  };
  resourceId?: string;
  subscriber: string;
  resourceType: string;
  actionNames?: string[];
  label?: string;
  resourceFilters: resourceFilter[];
};

enum Channel {
  EMAIL = 'email',
  HTTP = 'http',
  DIGEST = 'digest',
  INAPP = 'inapp',
}

// Digest subscriptions use cron-style day numbering: 0 = Sunday through 6 = Saturday.
const CRON_DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const formatChannelDisplay = (alertInfo: SubscriptionType['alertInfo']): string => {
  const { channel, details = {} } = alertInfo;
  const destination = details.email || details.httpEndpoint;

  if (channel !== Channel.DIGEST) {
    return [`(${channel})`, destination].filter(Boolean).join(' ');
  }

  const schedule =
    details.deliveryFrequency === 'WEEKLY' && details.dayOfWeek !== undefined
      ? `${details.deliveryFrequency} (${CRON_DAY_NAMES[details.dayOfWeek] || details.dayOfWeek})`
      : details.deliveryFrequency;

  return [`(${channel})`, [destination, schedule].filter(Boolean).join(' - ')]
    .filter(Boolean)
    .join(' ');
};

const Subscription: React.FC = () => {
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionType>();
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const removeSubscription = async (subscriptionId: string): Promise<void> => {
    try {
      await deleteSubscription(subscriptionId);
      setSubscriptionData(subscriptionData.filter(({ id }) => id !== subscriptionId));
    } catch (error) {
      console.error(error);
    }
  };

  const columns: Columns = [
    {
      Header: 'Resource Type',
      accessor: 'resourceType',
      maxWidth: 200,
      Cell: ({ original }) => (
        <Tooltip contents={original.resourceType} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.resourceType}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Action Names',
      accessor: 'actionNames',
      maxWidth: 150,
      Cell: ({ original }) => {
        const actionNames = (original.actionNames ?? []).join(', ');

        return (
          <Tooltip contents={actionNames} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
            <div>{actionNames}</div>
          </Tooltip>
        );
      },
    },
    {
      Header: 'Resource Id',
      accessor: 'resourceId',
      maxWidth: 400,
      Cell: ({ original }) => (
        <Tooltip contents={original.resourceId} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.resourceId}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Filter',
      accessor: 'resourceFilters',
      Cell: ({ original }) => (
        <CodeBlock code={JSON.stringify(original.resourceFilters, null, 2)} />
      ),
    },
    {
      Header: 'Metadata',
      accessor: 'metadata',
      Cell: ({ original }) => (
        <CodeBlock code={JSON.stringify(original?.metadata || [], null, 2)} />
      ),
    },
    {
      id: 'chanel',
      Header: 'Channel',
      maxWidth: 100,
      accessor: (data: SubscriptionType) => formatChannelDisplay(data.alertInfo),
      Cell: ({ original }) => (
        <Tooltip
          contents={formatChannelDisplay(original.alertInfo)}
          tooltipInnerStyle={{ minWidth: 'fit-content' }}
        >
          <div>{formatChannelDisplay(original.alertInfo)}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Subscriber',
      accessor: 'subscriber',
      maxWidth: 100,
      Cell: ({ original }) => (
        <Tooltip contents={original.subscriber} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.subscriber}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Created By',
      accessor: 'createdBy',
      maxWidth: 100,
      Cell: ({ original }) => (
        <Tooltip contents={original.createdBy} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.createdBy}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Created',
      accessor: 'created',
      maxWidth: 100,
      Cell: ({ original }: { original: SubscriptionType }) => (
        <Tooltip
          contents={`${new Date(original.created).toLocaleString()} (${original.created})`}
          tooltipInnerStyle={{ minWidth: 'fit-content' }}
        >
          <div>{new Date(original.created).toLocaleString()}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Action',
      maxWidth: 100,
      Cell: ({ original }) => (
        <>
          <Button
            variant="anchor"
            onClick={() => {
              setSelectedSubscription(original);
              setOpenModal(true);
            }}
          >
            <Tooltip contents={'Unsubscribe'}>
              <IconBin size={'lg'} weight={'fill'} />
            </Tooltip>
          </Button>
        </>
      ),
    },
  ];

  const fetchSubscriptions = async (): Promise<void> => {
    try {
      setLoadingData(true);
      const { subscriptions } = await getSubscriptions();

      setSubscriptionData(
        [...subscriptions].sort(
          (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  const refreshUserToken = (): void => {
    const authorizationCodeGrant = new AuthorizationCodeGrant({
      clientID: import.meta.env.VITE_NOTIFICATIONS_CLIENT_ID,
      redirectRoute: '/dashboard',
    });

    authorizationCodeGrant.login({ nextUri: `${import.meta.env.VITE_BASE_URL}/dashboard` });
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  return (
    <>
      <Modal
        status={'danger'}
        show={openModal}
        onRequestHide={() => setOpenModal(false)}
        closeButton={true}
        title={'Delete Subscription'}
        footer={
          <div className="" style={{ justifyContent: 'space-between' }}>
            <Button onClick={() => setOpenModal(false)} style={{ marginRight: '32px' }}>
              Close
            </Button>
            <Button
              variant={'primary'}
              onClick={() => {
                removeSubscription(selectedSubscription.id);
                setOpenModal(false);
              }}
            >
              {'Delete'}
            </Button>
          </div>
        }
      >
        <p>Deleting this subscription will permanently remove it and cannot be retrieved.</p>
      </Modal>
      <div className="flex-col">
        <div className="flex-row">
          <h1 style={{ margin: '16px 0' }}>My Subscriptions</h1>
          <Button
            style={{
              maxWidth: '200px',
              maxHeight: '48px',
              marginTop: 'auto',
              marginLeft: 'auto',
              marginBottom: '16px',
            }}
            onClick={refreshUserToken}
          >
            Refresh Token
          </Button>
        </div>
        <ReactTablev6
          minRows={(subscriptionData || []).length ? 0 : 10}
          columns={columns}
          data={subscriptionData}
          loading={loadingData}
          keyField="Resource Id"
        />
      </div>
    </>
  );
};

export default Subscription;
